/* eslint-disable default-param-last */
import { Reducer } from 'redux';
import produce from 'immer';
import { AiManagementTypes, AiManagementState } from './types';

const flags = (loading: boolean, loaded: boolean, error: boolean) => ({ loading, loaded, error });

const INITIAL_STATE: AiManagementState = {
  board: null,
  columns: [],
  cards: {},
  taskColumns: [],
  tasks: {},
  team: [],
  filters: {
    memberIds: [],
    scope: null,
    q: '',
    deadlineFrom: null,
    deadlineTo: null,
  },
  request: {
    columns: flags(false, false, false),
    cards: flags(false, false, false),
    tasks: flags(false, false, false),
    team: flags(false, false, false),
  },
};

const reducer: Reducer<AiManagementState> = (state = INITIAL_STATE, action) => {
  const { type, result, payload } = action as any;

  return produce(state, (draft) => {
    switch (type) {
      case AiManagementTypes.GET_COLUMNS_REQUEST:
        draft.request.columns = flags(true, state.request.columns.loaded, false);
        break;
      case AiManagementTypes.GET_COLUMNS_SUCCESS:
        draft.board = result?.data?.board ?? null;
        draft.columns = Array.isArray(result?.data?.columns) ? result.data.columns : [];
        draft.request.columns = flags(false, true, false);
        break;
      case AiManagementTypes.GET_COLUMNS_FAILURE:
        draft.request.columns = flags(false, false, true);
        break;
      case AiManagementTypes.REORDER_COLUMNS:
        draft.columns = Array.isArray(payload) ? payload : state.columns;
        break;
      case AiManagementTypes.RENAME_COLUMN: {
        const target = draft.columns.find((column) => column.id === payload.id);
        if (target) target.name = payload.name;
        break;
      }
      case AiManagementTypes.RECOLOR_COLUMN: {
        const target = draft.columns.find((column) => column.id === payload.id);
        if (target) target.color = payload.color;
        break;
      }
      case AiManagementTypes.REMOVE_COLUMN:
        draft.columns = state.columns.filter((column) => column.id !== payload.id);
        break;

      case AiManagementTypes.GET_CARDS_REQUEST:
        draft.request.cards = flags(true, state.request.cards.loaded, false);
        break;
      case AiManagementTypes.GET_CARDS_SUCCESS:
        draft.cards = result?.data?.cards ?? {};
        draft.request.cards = flags(false, true, false);
        break;
      case AiManagementTypes.GET_CARDS_FAILURE:
        draft.request.cards = flags(false, false, true);
        break;
      case AiManagementTypes.MOVE_CARD: {
        const { cardId, fromColumnId, toColumnId, toIndex } = payload;
        const source = draft.cards[fromColumnId];
        if (!source) break;
        const index = source.findIndex((card) => card.id === cardId);
        if (index === -1) break;
        const [card] = source.splice(index, 1);
        card.column_id = toColumnId;
        if (!draft.cards[toColumnId]) draft.cards[toColumnId] = [];
        draft.cards[toColumnId].splice(toIndex, 0, card);
        break;
      }
      case AiManagementTypes.REMOVE_CARD: {
        const { cardId } = payload;
        Object.keys(draft.cards).forEach((columnId) => {
          draft.cards[Number(columnId)] = draft.cards[Number(columnId)].filter(
            (card) => card.id !== cardId,
          );
        });
        break;
      }
      case AiManagementTypes.UPSERT_CARD: {
        Object.keys(draft.cards).forEach((columnId) => {
          const list = draft.cards[Number(columnId)];
          const index = list.findIndex((card) => card.id === payload.id);
          if (index !== -1) list[index] = payload;
        });
        break;
      }

      case AiManagementTypes.MOVE_TASK: {
        const { taskId, fromColumnId, toColumnId, toIndex } = payload;
        const source = draft.tasks[fromColumnId];
        if (!source) break;
        const index = source.findIndex((task) => task.id === taskId);
        if (index === -1) break;
        const [task] = source.splice(index, 1);
        task.task_column_id = toColumnId;
        if (!draft.tasks[toColumnId]) draft.tasks[toColumnId] = [];
        draft.tasks[toColumnId].splice(toIndex, 0, task);

        const from = draft.taskColumns.find((column) => column.id === fromColumnId);
        const to = draft.taskColumns.find((column) => column.id === toColumnId);
        if (from && to && fromColumnId !== toColumnId) {
          from.tasks_count = Math.max(0, from.tasks_count - 1);
          to.tasks_count += 1;
        }
        break;
      }
      case AiManagementTypes.GET_TASKS_REQUEST:
        draft.request.tasks = flags(true, state.request.tasks.loaded, false);
        break;
      case AiManagementTypes.GET_TASKS_SUCCESS:
        draft.taskColumns = Array.isArray(result?.data?.columns) ? result.data.columns : [];
        draft.tasks = result?.data?.tasks ?? {};
        draft.request.tasks = flags(false, true, false);
        break;
      case AiManagementTypes.GET_TASKS_FAILURE:
        draft.request.tasks = flags(false, false, true);
        break;

      case AiManagementTypes.GET_TEAM_REQUEST:
        draft.request.team = flags(true, state.request.team.loaded, false);
        break;
      case AiManagementTypes.GET_TEAM_SUCCESS:
        draft.team = Array.isArray(result?.data?.members) ? result.data.members : [];
        draft.request.team = flags(false, true, false);
        break;
      case AiManagementTypes.GET_TEAM_FAILURE:
        draft.request.team = flags(false, false, true);
        break;

      case AiManagementTypes.SET_FILTERS:
        draft.filters = payload;
        break;
      default:
    }
  });
};

export default reducer;
